import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { register } from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import TermsModal from './TermsModal';


const RegisterForm = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalType, setModalType] = useState<'terms' | 'privacy' | null>(null);



  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'password') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const calculatePasswordStrength = (password: string): number => {
    let score = 0;
    if (password.length > 8) score += 25;
    if (/[A-Z]/.test(password)) score += 25;
    if (/[0-9]/.test(password)) score += 25;
    if (/[^A-Za-z0-9]/.test(password)) score += 25;
    return score;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }
  
    if (passwordStrength < 50) {
      setError('Lütfen daha güçlü bir şifre belirleyin');
      return;
    }
  
    setIsLoading(true);
    setError(null);
  
    try {
      const response = await register({
        username: formData.username,
        email: formData.email,
        password: formData.password
      });
  
      console.log('Kayıt başarılı:', response);
      navigate('/login');
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      {/* Kullanıcı Adı Input */}
      <InputGroup>
        <InputLabel $isActive={focusedField === 'username' || formData.username.length > 0}>
          Kullanıcı Adı
        </InputLabel>
        <InputWrapper>
          <InputIcon>👤</InputIcon>
          <StyledInput
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            onFocus={() => setFocusedField('username')}
            onBlur={() => setFocusedField(null)}
            required
          />
          <InputLine $isActive={focusedField === 'username'} />
        </InputWrapper>
      </InputGroup>

      {/* Email Input */}
      <InputGroup>
        <InputLabel $isActive={focusedField === 'email' || formData.email.length > 0}>
          Email
        </InputLabel>
        <InputWrapper>
          <InputIcon>📧</InputIcon>
          <StyledInput
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onFocus={() => setFocusedField('email')}
            onBlur={() => setFocusedField(null)}
            required
          />
          <InputLine $isActive={focusedField === 'email'} />
        </InputWrapper>
      </InputGroup>

      {/* Şifre Input */}
      <InputGroup>
        <InputLabel $isActive={focusedField === 'password' || formData.password.length > 0}>
          Şifre
        </InputLabel>
        <InputWrapper>
          <InputIcon>🔒</InputIcon>
          <StyledInput
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
            required
          />
          <InputLine $isActive={focusedField === 'password'} />
        </InputWrapper>
        {formData.password && (
          <PasswordStrength>
<StrengthBar>
  <StrengthIndicator $strength={passwordStrength} />
</StrengthBar>
            <StrengthText>
              Şifre Gücü: {passwordStrength <= 25 ? 'Zayıf' : 
                          passwordStrength <= 50 ? 'Orta' : 
                          passwordStrength <= 75 ? 'İyi' : 'Güçlü'}
            </StrengthText>
          </PasswordStrength>
        )}
      </InputGroup>

      {/* Şifre Tekrar Input */}
      <InputGroup>
        <InputLabel $isActive={focusedField === 'confirmPassword' || formData.confirmPassword.length > 0}>
          Şifre Tekrar
        </InputLabel>
        <InputWrapper>
          <InputIcon>🔒</InputIcon>
          <StyledInput
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            onFocus={() => setFocusedField('confirmPassword')}
            onBlur={() => setFocusedField(null)}
            required
          />
          <InputLine $isActive={focusedField === 'confirmPassword'} />
        </InputWrapper>
        {formData.password && formData.confirmPassword && (
          <PasswordMatch $match={(formData.password === formData.confirmPassword).toString()}>
  <span>{formData.password === formData.confirmPassword ? '✓' : '✗'}</span>
  {formData.password === formData.confirmPassword ? 'Şifreler eşleşiyor' : 'Şifreler eşleşmiyor'}
</PasswordMatch>
        )}
      </InputGroup>

      {error && (
        <ErrorMessage>
          <ErrorIcon>⚠️</ErrorIcon>
          {error}
        </ErrorMessage>
      )}

      <SubmitButton type="submit" disabled={isLoading}>
        <ButtonContent>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <ButtonIcon>🚀</ButtonIcon>
              <span>Kayıt Ol</span>
            </>
          )}
        </ButtonContent>
        <ButtonGlow />
      </SubmitButton>

      <TermsContainer>
  <Checkbox type="checkbox" id="terms" required />
  <TermsText>
    <CheckboxLabel htmlFor="terms">
      <TermsLink onClick={() => setModalType('terms')}>
        Kullanım Şartları
      </TermsLink>
      'nı ve{' '}
      <TermsLink onClick={() => setModalType('privacy')}>
        Gizlilik Politikası
      </TermsLink>
      'nı kabul ediyorum
    </CheckboxLabel>
  </TermsText>
</TermsContainer>      
<TermsModal 
  type={modalType || 'terms'}
  isOpen={modalType !== null}
  onClose={() => setModalType(null)}
/>
</FormContainer>
  );
};

// Animasyonlar
const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const glowAnimation = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.02); }
`;

// Styled Components
const FormContainer = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${slideUp} 0.5s ease;
`;

const PasswordMatch = styled.div<{ $match: string }>`
  color: ${props => props.$match === 'true' ? '#52C41A' : '#FF4D4F'};
  font-size: 0.8rem;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 10px;
`;

const InputLabel = styled.label<{ $isActive: boolean }>`
  position: absolute;
  left: 45px;
  color: ${props => props.$isActive ? '#40A9FF' : '#8B9CC8'};
  font-size: ${props => props.$isActive ? '0.8rem' : '1rem'};
  top: ${props => props.$isActive ? '-20px' : '50%'};
  transform: translateY(-50%);
  transition: all 0.3s ease;
  pointer-events: none;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 15px;
  font-size: 1.2rem;
  color: #8B9CC8;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 15px 15px 15px 45px;
  background: rgba(64, 169, 255, 0.05);
  border: 1px solid transparent;
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    background: rgba(64, 169, 255, 0.1);
    border-color: #40A9FF;
  }
`;

const InputLine = styled.div<{ $isActive: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: 100%;
  background: linear-gradient(90deg, #40A9FF, #9D72FF);
  transform: scaleX(${props => props.$isActive ? 1 : 0});
  transition: transform 0.3s ease;
`;

const PasswordStrength = styled.div`
  margin-top: 5px;
`;

const StrengthBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(64, 169, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
`;

const StrengthIndicator = styled.div<{ $strength: number }>`
  width: ${props => props.$strength}%;
  height: 100%;
  background: ${props => 
    props.$strength <= 25 ? '#FF4D4F' :
    props.$strength <= 50 ? '#FAAD14' :
    props.$strength <= 75 ? '#52C41A' :
    '#40A9FF'
  };
  transition: all 0.3s ease;
`;


const StrengthText = styled.div`
  color: #8B9CC8;
  font-size: 0.8rem;
  margin-top: 5px;
`;

const SubmitButton = styled.button`
  position: relative;
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #40A9FF 0%, #9D72FF 100%);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  z-index: 1;
`;

const ButtonIcon = styled.span`
  font-size: 1.2rem;
`;

const ButtonGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #40A9FF 0%, #9D72FF 100%);
  filter: blur(15px);
  opacity: 0;
  transition: opacity 0.3s ease;

  ${SubmitButton}:hover:not(:disabled) & {
    opacity: 0.5;
  }
`;

const TermsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
`;

const Checkbox = styled.input`
  accent-color: #40A9FF;
  width: 16px;
  height: 16px;
  margin-top: 3px;
`;

const TermsText = styled.div`
  color: #8B9CC8;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CheckboxLabel = styled.label`
  color: #8B9CC8;
  font-size: 0.9rem;
  cursor: pointer;
`;

const TermsLink = styled.a`
  color: #40A9FF;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #FF4D4F;
  font-size: 0.9rem;
  padding: 10px;
  border-radius: 5px;
  background: rgba(255, 77, 79, 0.1);
  margin-bottom: 10px;
`;

const ErrorIcon = styled.span`
  font-size: 1.1rem;
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default RegisterForm;